import type { Space } from '@cocoonspace/sdk-js/types/space'
import type { Zone } from '@cocoonspace/sdk-js/types/zone'
import { useMemo } from 'react'
import {
	useGetSpace,
	useListSpaces,
} from '@cocoonspace/sdk-js/domains/spaces/spaces'
import { searchConfig } from '@cocoonspace/shared/config/search-config'
import { keepPreviousData } from '@tanstack/react-query'

export const useSpacesSuggestions = ({
	capacity,
	zone,
	position,
	enabled = true,
}: {
	capacity: number
	zone: Zone
	position: {
		latitude: number
		longitude: number
	}
	enabled?: boolean
}) => {
	return useListSpaces(
		{
			capacity: [`${Math.max((capacity ?? 0) - 2, 0)}-${(capacity ?? 0) + 2}`],
			limit: 6,
			zone,
			...position,
		},
		{
			query: {
				enabled: !!position && !!capacity && !!zone && enabled,
				placeholderData: keepPreviousData,
				select: ({ data }) => data,
			},
		},
	)
}

export const useSpaceDetailsPage = (spaceId: Space['id'] | Space['slug']) => {
	const spaceDetailsQuery = useGetSpace(spaceId, undefined, {
		query: {
			placeholderData: keepPreviousData,
			select: (space) => space.data,
		},
	})

	const district = useMemo(() => {
		const zone = (spaceDetailsQuery.data?.zone as Zone) || 'paris'

		return searchConfig.districts[zone]?.find(
			(district) =>
				district.zip === spaceDetailsQuery.data?.address.postal_code,
		)
	}, [
		spaceDetailsQuery.data?.zone,
		spaceDetailsQuery.data?.address.postal_code,
	])

	const suggestionsQuery = useListSpaces(
		{
			capacity: [
				`${Math.max((spaceDetailsQuery.data?.capacity ?? 0) - 2, 0)}-${
					(spaceDetailsQuery.data?.capacity ?? 0) + 2
				}`,
			],
			latitude: district?.latitude,
			limit: 6,
			longitude: district?.longitude,
			zone: spaceDetailsQuery.data?.zone as Zone,
		},
		{
			query: {
				enabled: !!spaceDetailsQuery.data && !!district,
				placeholderData: keepPreviousData,
				select: (spaces) => spaces.data,
			},
		},
	)

	return {
		district,
		spaceDetailsQuery,
		suggestionsQuery,
	}
}
